<template>
  <div class="tw-flex tw-flex-col">
    <ChatInputError :error="errorMessage" />
    <div
      class="tw-bg-gray-4 tw-py-2 tw-px-4 tw-flex tw-flex-nowrap tw-w-full tw-items-center"
      :class="{ 'is-error': errorMessage }"
    >
      <input
        id="input-file"
        ref="inputRef"
        :key="inputKey"
        :accept="ACCEPT_DOCUMENT_RULES_WITH_PDF"
        type="file"
        name="file"
        data-input-file
        multiple
        class="tw-hidden"
        @change="handleFileChanged"
      />
      <AppIcon
        shape="paperclip"
        cursor="pointer"
        class="tw-mr-2"
        @click="inputRef?.click()"
      />
      <el-dropdown trigger="click" :disabled="!fileList.length">
        <el-badge :value="fileList.length" type="primary">
          <AppIcon shape="pinboard" />
        </el-badge>

        <template #dropdown>
          <div class="tw-p-2">
            <AppText as="p" size="lg" weight="bold" color="black">
              {{ t('pt.aiInput.attachment.title') }}
            </AppText>
            <div
              v-for="item in fileList"
              :key="item.originalFilename"
              class="tw-flex tw-items-center tw-text-nowrap"
            >
              <AppIcon shape="file" size="16" class="tw-mr-2" />
              <AppText as="div" color="black" class="tw-mr-1">
                {{ item.originalFilename }}
              </AppText>
              <AppText as="div" whitespace="nowrap">
                {{ t('fileSize', { size: formatFileSize(item.size) }) }}
              </AppText>
            </div>
          </div>
        </template>
      </el-dropdown>

      <TextInput
        :input-type="MessagePayload_InputType.CUSTOM"
        is-hipaa
        class="tw-ml-4"
        @vnode-mounted="!isLastAnimatedMessage && emit('loaded')"
        @send-message="sendMessage"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ElMessage } from 'element-plus'
import { defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ChatInputError from '@shared/components/ChatInputError.vue'
import { ACCEPT_DOCUMENT_RULES_WITH_PDF } from '@shared/constants/file-uploading-types'
import { FileSizeType } from '@shared/models/enums/file-size-type'
import { MessageCreateData } from '@shared/models/interfaces/messages'
import { IFileUploadExtended } from '@shared/models/proto/base/file-upload'
import { Nullable } from '@shared/models/types/primary'
import { useChatInputActions } from '@shared/utils/composables/chat-messages'
import { useFetchData } from '@shared/utils/composables/fetch-data'
import {
  fileSizeToBytes,
  formatFileSize,
} from '@shared/utils/helpers/file-size-formatter'
import {
  sessionStorageGet,
  sessionStorageHas,
  SessionStorageKeys,
  sessionStorageSave,
} from '@shared/utils/helpers/storage/session-storage'
import { generateUint8ArrayFromFile } from '@shared/utils/helpers/uint8array-formatters'

import { MessagePayload_InputType } from '@protos/chat/message_payload_pb'

import { useChatMessages } from '@pc/composables/use-chat-messages'
import { MessageCreateDataDTO } from '@pc/models/proto/dto/messages'
import { usePdfsStore } from '@pc/store/pdfs'

const TextInput = defineAsyncComponent(
  () => import('@pc/components/ChatBotInput/components/TextInput.vue')
)

const props = withDefaults(
  defineProps<{
    conversationId: number
    loading?: boolean
  }>(),
  {
    allowedTypes: () => [],
    loading: false,
  }
)
const emit = defineEmits<{
  (event: 'send-message', payload: MessageCreateData): void
  (event: 'loaded'): void
}>()

const { t } = useI18n()
const { errorMessage, isLastAnimatedMessage } = useChatMessages()
const { files } = useChatInputActions()
const { fetchData } = useFetchData()
const pdfsStore = usePdfsStore()

const inputRef = ref<Nullable<HTMLInputElement>>(null)
const inputKey = ref(0)
const fileList = ref<IFileUploadExtended[]>([])

const getDtoFiles = async (files: File[]) => {
  const dtoFiles = []

  for (const file of files) {
    if (!ACCEPT_DOCUMENT_RULES_WITH_PDF.includes(file.type)) {
      ElMessage.error(t('pt.messages.errors.unsupportedTypes'))
    } else {
      if (file.size > fileSizeToBytes(5, FileSizeType.MB)) {
        ElMessage.error(t('pt.messages.errors.fileSize'))
      } else {
        const content = await generateUint8ArrayFromFile(file)
        dtoFiles.push({
          content,
          originalFilename: file.name,
          contentType: file.type,
          size: file.size,
        })
      }
    }
  }

  return dtoFiles
}

const sendMessage = ({
  text,
  messageType,
  payload,
}: Pick<MessageCreateDataDTO, 'text' | 'messageType' | 'payload'>) => {
  emit('send-message', {
    message: {
      text,
      messageType,
      payload,
      attachments: [],
    },
    files: [],
    isMessageToAIAgent: true,
  })
}

const uploadAiFiles = (dtoFiles: IFileUploadExtended[]) => {
  fetchData({
    onFetchDataCallback: async () => {
      if (dtoFiles.length) {
        await pdfsStore.createPdf({
          conversationId: props.conversationId,
          files: dtoFiles,
        })

        ElMessage.success(t('pt.messages.success.uploaded'))

        fileList.value = [...fileList.value, ...dtoFiles]
        sessionStorageSave(
          SessionStorageKeys.AiInputAttachments,
          JSON.stringify(fileList.value)
        )
      }
    },
  })
}

const handleFileChanged = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = input.files
  inputKey.value++

  if (files?.length) {
    uploadAiFiles(
      (await getDtoFiles(Array.from(files))) as IFileUploadExtended[]
    )
  }
}

watch(
  files,
  async (value) => {
    if (value.length) {
      uploadAiFiles((await getDtoFiles(value)) as IFileUploadExtended[])
      files.value = []
    }
  },
  { deep: true }
)

onMounted(() => {
  if (sessionStorageHas(SessionStorageKeys.AiInputAttachments)) {
    fileList.value = JSON.parse(
      sessionStorageGet(SessionStorageKeys.AiInputAttachments)
    )
  }
})
</script>

<style lang="scss" scoped>
:deep(.is-error .el-input__wrapper) {
  @apply tw-border-red-500 tw-border tw-border-solid;

  &.is-focus {
    border-color: $error;
    box-shadow: 0 0 1px 1px #ff5252;
  }
}
</style>
